import React, { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconPlay, IconVideo } from '@/lib/icons';
import { Play, X } from 'lucide-react';

interface Video {
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail: string;
  showInCareView: boolean;
}

export default function CareRecipientVideos({ hubId: propHubId }: { hubId?: string }) {
  const params = useParams();
  const hubId = propHubId || params.hubId || new URLSearchParams(window.location.search).get('hubId') || '';
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  useEffect(() => {
    if (hubId) {
      loadVideos();
      
      // Set up polling to check for updates every 500ms
      const interval = setInterval(loadVideos, 5000);
      return () => clearInterval(interval);
    }
  }, [hubId]);

  const loadVideos = async () => {
    try {
      console.log(`[Care Recipient Videos] Loading videos for hub ${hubId} from server...`);
      
      const careToken = localStorage.getItem(`carePin_${hubId}`) || sessionStorage.getItem(`carePin_${hubId}`);
      const response = await fetch(`/api/care/videos?hubId=${hubId}`, {
        headers: careToken ? { "x-care-token": careToken } : {},
      });
      if (response.ok) {
        const serverVideos = await response.json();
        console.log(`[Care Recipient Videos] Loaded videos from server:`, serverVideos);
        setVideos(Array.isArray(serverVideos) ? serverVideos : []);
      } else {
        console.error(`[Care Recipient Videos] Server returned ${response.status}`);
        setVideos([]);
      }
    } catch (error) {
      console.error(`[Care Recipient Videos] Error loading videos:`, error);
      setVideos([]);
    }
  };

  const handlePlayVideo = (video: Video) => {
    console.log(`[Care Recipient Videos] Opening video player for: ${video.title}`);
    setSelectedVideo(video);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">My Videos</h2>
        <p className="text-gray-600">Watch demonstration videos</p>
      </div>

      {/* Videos Grid */}
      {videos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {videos.map((video) => (
            <Card 
              key={video.id} 
              className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handlePlayVideo(video)}
            >
              <div className="relative">
                {video.url ? (
                  <video
                    src={video.url}
                    className="w-full h-32 object-cover"
                    preload="metadata"
                    muted
                    playsInline
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                    <IconVideo className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                  <IconPlay className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{video.title}</CardTitle>
              </CardHeader>
              
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600">{video.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <IconVideo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">You're all caught up for now.</p>
          <p className="text-gray-400 text-sm mt-2">You'll see something new when it's shared with you.</p>
        </div>
      )}

      {/* CRITICAL FIX 3: Real HTML5 Video Player Modal instead of alerts */}
      {selectedVideo && (
        <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <DialogContent className="sm:max-w-[800px]">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedVideo.title}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedVideo(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video 
                  className="w-full h-full"
                  controls
                  autoPlay
                  preload="auto"
                  src={selectedVideo.url}
                  onError={(e) => {
                    console.error('[CareRecipientVideos] Video load error:', selectedVideo.url);
                  }}
                >
                  <source src={selectedVideo.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {selectedVideo.description && (
                <div className="space-y-2">
                  <p className="text-gray-600 text-sm">
                    {selectedVideo.description}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}